import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { makeStyles } from "@material-ui/core/styles";

import "./ModalImageCors.css";
import { resolveMediaUrl } from "../../helpers/resolveMediaUrl";

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

const useStyles = makeStyles(() => ({
	messageMedia: {
		objectFit: "cover",
		width: 250,
		height: 200,
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
		paddingBottom: 6,
		cursor: "zoom-in",
		display: "block",
	},
}));

function ImageLightbox({ src, alt, onClose }) {
	const [zoom, setZoom] = useState(1);
	const [rotation, setRotation] = useState(0);
	const [offset, setOffset] = useState({ x: 0, y: 0 });
	const stageRef = useRef(null);
	const imgRef = useRef(null);
	const zoomRef = useRef(1);
	const offsetRef = useRef({ x: 0, y: 0 });
	const draggingRef = useRef(false);
	const lastPointRef = useRef({ x: 0, y: 0 });
	const pinchRef = useRef(null);
	const lastTapRef = useRef({ time: 0, x: 0, y: 0 });
	const movedRef = useRef(false);

	const clampZoom = useCallback((value) => {
		return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
	}, []);

	const applyZoom = useCallback(
		(nextZoom, nextOffset) => {
			const zoomValue = clampZoom(nextZoom);
			const offsetValue =
				zoomValue <= MIN_ZOOM
					? { x: 0, y: 0 }
					: nextOffset || offsetRef.current;
			zoomRef.current = zoomValue;
			offsetRef.current = offsetValue;
			setZoom(zoomValue);
			setOffset(offsetValue);
		},
		[clampZoom]
	);

	const zoomIn = useCallback(() => {
		applyZoom(zoomRef.current + ZOOM_STEP);
	}, [applyZoom]);

	const zoomOut = useCallback(() => {
		applyZoom(zoomRef.current - ZOOM_STEP);
	}, [applyZoom]);

	const resetZoom = useCallback(() => {
		applyZoom(1, { x: 0, y: 0 });
	}, [applyZoom]);

	const rotateLeft = useCallback(() => {
		setRotation((current) => (current - 90 + 360) % 360);
		applyZoom(1, { x: 0, y: 0 });
	}, [applyZoom]);

	const rotateRight = useCallback(() => {
		setRotation((current) => (current + 90) % 360);
		applyZoom(1, { x: 0, y: 0 });
	}, [applyZoom]);

	const toggleDoubleTapZoom = useCallback(() => {
		if (zoomRef.current > MIN_ZOOM) {
			applyZoom(1, { x: 0, y: 0 });
			return;
		}
		applyZoom(2.5);
	}, [applyZoom]);

	useEffect(() => {
		const onKeyDown = (event) => {
			if (event.key === "Escape") onClose();
			if (event.key === "+" || event.key === "=") zoomIn();
			if (event.key === "-") zoomOut();
			if (event.key === "0") resetZoom();
			if (event.key === "r" || event.key === "R") rotateRight();
			if (event.key === "l" || event.key === "L") rotateLeft();
		};

		document.addEventListener("keydown", onKeyDown);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		return () => {
			document.removeEventListener("keydown", onKeyDown);
			document.body.style.overflow = previousOverflow;
		};
	}, [onClose, zoomIn, zoomOut, resetZoom, rotateLeft, rotateRight]);

	useEffect(() => {
		const stage = stageRef.current;
		if (!stage) return undefined;

		const getDistance = (touches) => {
			const [a, b] = touches;
			const dx = a.clientX - b.clientX;
			const dy = a.clientY - b.clientY;
			return Math.hypot(dx, dy);
		};

		const onTouchStart = (event) => {
			movedRef.current = false;

			if (event.touches.length === 2) {
				event.preventDefault();
				draggingRef.current = false;
				pinchRef.current = {
					startDistance: getDistance(event.touches),
					startZoom: zoomRef.current,
				};
				return;
			}

			if (event.touches.length === 1) {
				const touch = event.touches[0];
				lastPointRef.current = { x: touch.clientX, y: touch.clientY };
				draggingRef.current = zoomRef.current > MIN_ZOOM;
			}
		};

		const onTouchMove = (event) => {
			if (event.touches.length === 2 && pinchRef.current) {
				event.preventDefault();
				movedRef.current = true;
				const distance = getDistance(event.touches);
				const ratio = distance / Math.max(pinchRef.current.startDistance, 1);
				applyZoom(pinchRef.current.startZoom * ratio);
				return;
			}

			if (event.touches.length === 1 && draggingRef.current) {
				event.preventDefault();
				const touch = event.touches[0];
				const dx = touch.clientX - lastPointRef.current.x;
				const dy = touch.clientY - lastPointRef.current.y;
				if (Math.abs(dx) > 2 || Math.abs(dy) > 2) movedRef.current = true;
				lastPointRef.current = { x: touch.clientX, y: touch.clientY };
				applyZoom(zoomRef.current, {
					x: offsetRef.current.x + dx,
					y: offsetRef.current.y + dy,
				});
			}
		};

		const onTouchEnd = (event) => {
			if (event.touches.length < 2) {
				pinchRef.current = null;
			}
			if (event.touches.length === 0) {
				draggingRef.current = false;
			}
		};

		stage.addEventListener("touchstart", onTouchStart, { passive: false });
		stage.addEventListener("touchmove", onTouchMove, { passive: false });
		stage.addEventListener("touchend", onTouchEnd);
		stage.addEventListener("touchcancel", onTouchEnd);

		return () => {
			stage.removeEventListener("touchstart", onTouchStart);
			stage.removeEventListener("touchmove", onTouchMove);
			stage.removeEventListener("touchend", onTouchEnd);
			stage.removeEventListener("touchcancel", onTouchEnd);
		};
	}, [applyZoom]);

	const handleWheel = (event) => {
		event.preventDefault();
		if (event.deltaY < 0) zoomIn();
		else zoomOut();
	};

	const handlePointerDown = (event) => {
		if (event.pointerType === "touch") return;
		if (zoom <= MIN_ZOOM) return;
		draggingRef.current = true;
		lastPointRef.current = { x: event.clientX, y: event.clientY };
		event.currentTarget.setPointerCapture?.(event.pointerId);
	};

	const handlePointerMove = (event) => {
		if (event.pointerType === "touch") return;
		if (!draggingRef.current || zoom <= MIN_ZOOM) return;
		const dx = event.clientX - lastPointRef.current.x;
		const dy = event.clientY - lastPointRef.current.y;
		lastPointRef.current = { x: event.clientX, y: event.clientY };
		applyZoom(zoomRef.current, {
			x: offsetRef.current.x + dx,
			y: offsetRef.current.y + dy,
		});
	};

	const handlePointerUp = (event) => {
		if (event.pointerType === "touch") return;
		draggingRef.current = false;
	};

	const handleImageClick = (event) => {
		event.stopPropagation();

		const now = Date.now();
		const { time, x, y } = lastTapRef.current;
		const dx = Math.abs(event.clientX - x);
		const dy = Math.abs(event.clientY - y);
		const isDoubleTap = now - time < 300 && dx < 40 && dy < 40;

		lastTapRef.current = { time: now, x: event.clientX, y: event.clientY };

		if (isDoubleTap) {
			toggleDoubleTapZoom();
			lastTapRef.current = { time: 0, x: 0, y: 0 };
		}
	};

	const handleStageClick = (event) => {
		if (movedRef.current) {
			movedRef.current = false;
			return;
		}
		if (event.target === imgRef.current) return;
		onClose();
	};

	return ReactDOM.createPortal(
		<div
			className="chat-image-lightbox"
			role="dialog"
			aria-modal="true"
			aria-label="Visualizar imagem"
			onClick={onClose}
		>
			<div
				className="chat-image-lightbox__toolbar"
				onClick={(event) => event.stopPropagation()}
			>
				<button type="button" onClick={zoomOut} aria-label="Diminuir zoom" title="Diminuir zoom">
					−
				</button>
				<span className="chat-image-lightbox__zoom-label">{Math.round(zoom * 100)}%</span>
				<button type="button" onClick={zoomIn} aria-label="Aumentar zoom" title="Aumentar zoom">
					+
				</button>
				<button type="button" onClick={resetZoom} aria-label="Resetar zoom" title="Resetar zoom">
					1:1
				</button>
				<button
					type="button"
					onClick={rotateLeft}
					aria-label="Girar para a esquerda"
					title="Girar para a esquerda"
				>
					↺
				</button>
				<button
					type="button"
					onClick={rotateRight}
					aria-label="Girar para a direita"
					title="Girar para a direita"
				>
					↻
				</button>
				<a
					href={src}
					target="_blank"
					rel="noopener noreferrer"
					aria-label="Abrir original"
					title="Abrir original"
				>
					↗
				</a>
				<button type="button" onClick={onClose} aria-label="Fechar" title="Fechar">
					×
				</button>
			</div>

			<div
				ref={stageRef}
				className="chat-image-lightbox__stage"
				onClick={(event) => {
					event.stopPropagation();
					handleStageClick(event);
				}}
				onWheel={handleWheel}
			>
				<img
					ref={imgRef}
					src={src}
					alt={alt || "image"}
					className={`chat-image-lightbox__img${
						rotation % 180 !== 0 ? " chat-image-lightbox__img--sideways" : ""
					}`}
					draggable={false}
					onClick={handleImageClick}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerUp}
					onPointerCancel={handlePointerUp}
					style={{
						transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
						cursor: zoom > MIN_ZOOM ? "grab" : "default",
					}}
				/>
			</div>
		</div>,
		document.body
	);
}

function ModalImageCors({ imageUrl }) {
	const classes = useStyles();
	const [finalUrl, setFinalUrl] = useState("");
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!imageUrl) return;
		setFinalUrl(resolveMediaUrl(imageUrl));
	}, [imageUrl]);

	if (!finalUrl) return null;

	return (
		<>
			<img
				src={finalUrl}
				alt="image"
				className={classes.messageMedia}
				onClick={() => setOpen(true)}
			/>
			{open && (
				<ImageLightbox
					src={finalUrl}
					alt="image"
					onClose={() => setOpen(false)}
				/>
			)}
		</>
	);
}

export default ModalImageCors;
